import {
    TypedUseSelectorHook,
    useDispatch as useDispatchGeneric,
    useSelector as useSelectorGeneric,
} from "react-redux";
import { Dispatch, Store } from "redux";
import { Epic as GenericEpic, StateObservable } from "redux-observable";

import { Action } from "../actions";
import { App } from "../app/App";
import { rootEpic } from "../epics";
import { RootState } from "../reducers";
import { getFlag } from "../reducers/persist";
import { App as app } from "../ui/Main";

export type ExtraArguments = { App: App; Image: typeof Image };

export type RootStore = Store<RootState>;
export type RootStateObservable = StateObservable<RootState>;

export type Epic = GenericEpic<Action, Action, RootState, ExtraArguments>;

export type RootEpic = typeof rootEpic;

interface MapStateToProps<TStateProps, TOwnProps, State> {
    (state: State, ownProps: TOwnProps): TStateProps;
}

interface MapStateToPropsFactory<TStateProps, TOwnProps, State> {
    (initialState: State, ownProps: TOwnProps): MapStateToProps<TStateProps, TOwnProps, State>;
}

const debug = getFlag(app.rootStore.getState(), "map_to_props_log");

type MapStateToPropsParam<TStateProps, TOwnProps, State> =
    | MapStateToPropsFactory<TStateProps, TOwnProps, State>
    | MapStateToProps<TStateProps, TOwnProps, State>;

export function reduxType<S, D, OwnProps>(
    mapStoreToProps: MapStateToPropsParam<S, OwnProps, RootState>,
    mapDispatchToProps: D
) {
    return {
        mapStoreToProps: debug ? wrap(mapStoreToProps) : mapStoreToProps,
        mapDispatchToProps,
        // tslint:disable-next-line:no-null-keyword
        propsType: (null as any) as OwnProps & S & D,
    };
}

export const wrap = <S, OwnProps, State>(
    mapStateToProps: MapStateToPropsParam<S, OwnProps, State>,
    title = mapStateToProps.toString()
) => {
    return ((store, props) => {
        const x = trace(() => mapStateToProps(store, props), title);
        if (!isState(x)) {
            return ((store, props) => {
                return trace(() => x(store, props), title);
            }) as typeof mapStateToProps;
        }
        return x;
    }) as typeof mapStateToProps;
};

const thresholdMs = 1;
function trace<R>(f: () => R, title: string) {
    const start = performance.now();
    window.performance.mark("mapStoreToProps_start");
    const res = f();
    window.performance.mark("mapStoreToProps_end");
    window.performance.measure(
        "🦆 mapStoreToProps " + title,
        "mapStoreToProps_start",
        "mapStoreToProps_end"
    );
    const end = performance.now();
    if (end > start + thresholdMs) {
        console.warn("slow", title, end - start);
    }
    return res;
}

function isState<TStateProps, TOwnProps, State>(
    x: TStateProps | MapStateToProps<TStateProps, TOwnProps, State>
): x is TStateProps {
    return typeof x === "object";
}

export const useSelector: TypedUseSelectorHook<RootState> = useSelectorGeneric;
export const useDispatch: () => Dispatch<Action> = useDispatchGeneric;
